/**
 * Rebuy Side Cart Integration
 * 
 * This script handles opening Rebuy Side Cart when products are added to cart
 * on pages without a standard Shopify cart drawer.
 * 
 * It prevents redirects to the cart page and opens Rebuy cart instead.
 */

(function() {
  'use strict';

  let rebuyCartOpened = false;

  // Check if Rebuy is available
  function isRebuyAvailable() {
    return typeof Rebuy !== 'undefined' && Rebuy.SmartCart;
  }

  // Check if there's a standard cart drawer
  function hasCartDrawer() {
    return document.querySelector('cart-drawer, cart-notification') !== null;
  }

  // Open Rebuy cart
  function openRebuyCart() {
    if (isRebuyAvailable() && !rebuyCartOpened) {
      try {
        rebuyCartOpened = true;

        // Scroll position is preserved by the body scroll-lock (see bottom of file),
        // which captures the pre-open position and restores it on close.
        Rebuy.SmartCart.show();

        // Debounce reset to avoid rapid double-opens.
        setTimeout(function() {
          rebuyCartOpened = false;
        }, 250);

        return true;
      } catch (error) {
        console.error('Error opening Rebuy cart:', error);
        rebuyCartOpened = false;
        return false;
      }
    }
    return false;
  }

  // Override routes.cart_url to prevent redirects
  function overrideCartRoute() {
    if (typeof window.routes === 'undefined') {
      window.routes = {};
    }

    // Store original cart_url if it exists
    const originalCartUrl = window.routes.cart_url || '/cart';

    // Override the getter for cart_url
    Object.defineProperty(window.routes, 'cart_url', {
      get: function() {
        // If no cart drawer and Rebuy is available, return a no-op value
        if (!hasCartDrawer() && isRebuyAvailable()) {
          return '#rebuy-cart';
        }
        return originalCartUrl;
      },
      configurable: true
    });
  }

  // Intercept navigation attempts
  function interceptNavigation() {
    // Intercept clicks on links that would go to cart
    document.addEventListener('click', function(e) {
      const target = e.target.closest('a');
      if (target && target.href) {
        const href = target.href;
        if ((href.includes('/cart') || href === window.location.origin + '/cart') && !hasCartDrawer()) {
          e.preventDefault();
          if (openRebuyCart()) return;
          if (typeof window.loadRbsRebuy === 'function') {
            window.loadRbsRebuy()
              .then(function() {
                if (!openRebuyCart()) window.location = window.routes.cart_url;
              })
              .catch(function(error) {
                console.error('Failed to load Rebuy Smart Cart:', error);
                window.location = window.routes.cart_url;
              });
          } else {
            window.location = window.routes.cart_url;
          }
        }
      }
    }, true);
  }

  // Monitor for location changes
  function monitorLocationChanges() {
    let currentUrl = window.location.href;

    // Use MutationObserver as a fallback to detect navigation
    const observer = new MutationObserver(function() {
      const newUrl = window.location.href;
      if (newUrl !== currentUrl) {
        currentUrl = newUrl;
        // If we navigated to cart page and Rebuy is available, open cart
        if (newUrl.includes('/cart') && !hasCartDrawer() && isRebuyAvailable()) {
          // Go back and open Rebuy cart
          window.history.back();
          setTimeout(openRebuyCart, 100);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Patch window.location.href setter
  function patchLocationHref() {
    const location = window.location;
    const originalHref = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(location), 'href');

    if (originalHref && originalHref.set) {
      Object.defineProperty(location, 'href', {
        get: originalHref.get,
        set: function(url) {
          // Check if it's a redirect to cart page
          if (typeof url === 'string' && (url.includes('/cart') || url === '/cart')) {
            if (!hasCartDrawer() && isRebuyAvailable()) {
              // Open Rebuy cart instead
              if (openRebuyCart()) {
                return; // Prevent redirect
              }
            }
          }
          // Allow other redirects
          originalHref.set.call(location, url);
        },
        configurable: true
      });
    }
  }

  // Initialize
  function init() {
    // Only set up if there's no cart drawer
    if (hasCartDrawer()) {
      return; // Don't interfere with standard cart drawer
    }

    // Set up overrides immediately
    overrideCartRoute();
    interceptNavigation();

    // If another script has already loaded Rebuy, enable the legacy redirect guards.
    if (isRebuyAvailable()) {
      patchLocationHref();
      monitorLocationChanges();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/**
 * Rebuy Smart Cart — iOS Safari body scroll-lock
 *
 * On iOS Safari, `position: fixed` overlays (Rebuy's #rebuy-cart) do not stop
 * touch-scroll from bleeding through to the page behind them. The reliable fix
 * is to pin <body> with position:fixed while the cart is open and restore the
 * scroll position on close.
 *
 * Open/close is signalled by the `is-visible` class on the #rebuy-cart element
 * (which Rebuy keeps mounted in the DOM at all times). Because Rebuy injects
 * that element asynchronously, we wait for it to exist before attaching a
 * focused class observer.
 *
 * Runs unconditionally (independent of the cart-integration init() above, which
 * early-returns when a theme cart-drawer is present).
 */
(function() {
  'use strict';

  var CART_ID = 'rebuy-cart';
  var VISIBLE_CLASS = 'is-visible';
  var MOBILE_QUERY = '(max-width: 749px)'; // theme mobile breakpoint

  var cartEl = null;
  var isLocked = false;
  // Last scroll position recorded while the cart was CLOSED. Rebuy can focus-jump
  // the page to the bottom on open; that scroll event fires after the cart opens,
  // so guarding on cartIsVisible() keeps this value at the genuine pre-open spot.
  var lastStableScrollY = window.scrollY || window.pageYOffset || 0;

  function cartIsVisible() {
    return !!cartEl && cartEl.classList.contains(VISIBLE_CLASS);
  }

  window.addEventListener('scroll', function() {
    if (!cartIsVisible()) {
      lastStableScrollY = window.scrollY || window.pageYOffset || 0;
    }
  }, { passive: true });

  function lockBody() {
    if (isLocked) return;
    if (!window.matchMedia(MOBILE_QUERY).matches) return; // mobile only
    isLocked = true;
    var body = document.body;
    body.style.position = 'fixed';
    body.style.top = -lastStableScrollY + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }

  function unlockBody() {
    if (!isLocked) return;
    isLocked = false;
    var body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    window.scrollTo(0, lastStableScrollY);
  }

  function syncLock() {
    if (cartIsVisible()) {
      lockBody();
    } else {
      unlockBody();
    }
  }

  // Focused observer: only watches the cart element's class.
  function attachCartObserver(el) {
    cartEl = el;
    new MutationObserver(syncLock).observe(el, {
      attributes: true,
      attributeFilter: ['class']
    });
    syncLock(); // in case it's already open when we attach
  }

  // Bootstrap: Rebuy injects #rebuy-cart asynchronously, so wait for it.
  var existing = document.getElementById(CART_ID);
  if (existing) {
    attachCartObserver(existing);
  } else {
    var bootstrap = new MutationObserver(function() {
      var el = document.getElementById(CART_ID);
      if (el) {
        bootstrap.disconnect();
        attachCartObserver(el);
      }
    });
    bootstrap.observe(document.body, { childList: true, subtree: true });
  }
})();
